import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useSwipeable } from 'react-swipeable';
import _ from 'lodash';
import NarrativeStep from 'components/NarrativeStep';
import Loading from 'components/LoadingComponent';
import FinalStep from 'components/Steps/FinalStep';
import { ROUTE_MAIN_PAGE } from 'constants/urls';
import { getNarrativeNextStep, setStepAsViewed } from 'modules/steps/actions';
import {
  NarrativeStyled, StepContent, Buttons, LoadingWrapper,
} from './styles';

const deltaToScroll = 100;
function Narrative(props) {
  const {
    id, literals, actualContext, history, setSerendipityActionInContext,
    getSerendipityAction, setNarrativeAsCompleted,
  } = props;

  const [positionY, setPositionY] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isChangingStep, setIsChangingStep] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [deltaToStart, setDeltaToStart] = useState(null);
  const [isTheEnd, setIsTheEnd] = useState(false);
  const [stepsViewed, setStepsViewed] = useState([]);

  const [steps, setSteps] = useState([]);
  const [actualStep, setActualStep] = useState(0);

  const [isLoading, setIsLoading] = useState(false);

  const loadNextStep = (callback) => {
    const currentStep = steps.length ? _.last(steps).id : 0;
    if (!isLoading) {
      setIsLoading(true);
      getNarrativeNextStep(id, currentStep)
        .then((response) => {
          steps.push(response);
          setSteps([...steps]);
          setIsLoading(false);
          if (actualStep >= steps.length - 1) {
            // Preload next step
            if (typeof response.more_info.wait_to_answer === 'undefined' || !response.more_info.wait_to_answer) {
              loadNextStep();
            }
          }

          if (callback) {
            callback();
          }
        })
        .catch(() => {
          setIsTheEnd(true);
          setIsLoading(false);
          setNarrativeAsCompleted(id);

          if (callback) {
            callback();
          }
        });
    }
  };

  // Load First step
  useEffect(() => {
    setSteps([]);
    setActualStep(0);
  }, [id]);

  useEffect(() => {
    return () => {
      setSerendipityActionInContext(null);
    };
  }, []);

  useEffect(() => {
    window.$('.narrative_step:first').removeClass('no_transition').css('opacity', 1);
    setIsChangingStep(false);

    if (actualStep >= steps.length - 1) {
      const lastStep = steps.length ? steps[steps.length - 1] : false;
      if (!lastStep || typeof lastStep.more_info.wait_to_answer === 'undefined' || !lastStep.more_info.wait_to_answer) {
        loadNextStep();
      }
    }
  }, [actualStep]);

  const pushStepAsViewed = (stepId) => {
    if (!stepsViewed.includes(stepId)) {
      stepsViewed.push(stepId);
      setStepsViewed([...stepsViewed]);
      setStepAsViewed(stepId, id);
    }
  };

  const setAnswers = (answers) => {
    steps[actualStep].answers = answers;
    setSteps([...steps]);
  };

  // Events
  const handleSwiping = (event) => {
    if (deltaToStart === null) {
      setDeltaToStart(event.deltaY);
    } else if (!isScrolling) {
      if (!isSwiping) {
        setIsSwiping(true);
      }
      const diff = event.deltaY - deltaToStart;
      setPositionY(diff);
    }
  };

  const changeStep = (newPosition) => {
    setTimeout(() => {
      window.$('.narrative_step:first').addClass('no_transition').css('opacity', 0);
      if (newPosition > actualStep) {
        setPositionY(window.$('.narrative_step:first').outerHeight());
      } else {
        setPositionY(window.$('.narrative_step:first').outerHeight() * (-1));
      }
      window.$('.narrative_step:first').addClass('no_transition').css('opacity', 1);
      setTimeout(() => {
        setActualStep(newPosition);
        setPositionY(0);
      }, 50);
    }, 600);
  };

  // useEffect(() => {
  //   if (steps.length >= 2 && typeof steps[steps.length - 2].more_info.wait_to_answer !== 'undefined' && steps[steps.length - 2].more_info.wait_to_answer) {
  //     changeStep(actualStep + 1);
  //   }
  // }, [steps]);

  const goUp = () => {
    if (isTheEnd || actualStep < steps.length) {
      if (actualStep === steps.length && isTheEnd) {
        setSerendipityActionInContext(null);
        const action = getSerendipityAction();
        if (action) {
          action();
        } else {
          history.push(ROUTE_MAIN_PAGE);
        }
      } else if (actualStep === (steps.length - 1) && !isLoading) {
        loadNextStep(() => {
          changeStep(actualStep + 1);
        });
        setIsChangingStep(true);
        setIsSwiping(false);
        setPositionY(window.$('.narrative_step:first').outerHeight() * (-1));
      } else {
        setIsChangingStep(true);
        setIsSwiping(false);
        setPositionY(window.$('.narrative_step:first').outerHeight() * (-1));
        changeStep(actualStep + 1);
      }
    }
  };

  const goDown = () => {
    if (!isChangingStep) {
      if (actualStep > 0) {
        setIsChangingStep(true);
        setIsSwiping(false);
        setPositionY(window.$('.narrative_step:first').outerHeight());
        changeStep(actualStep - 1);
      } else {
        setIsSwiping(false);
      }
    }
  };

  useEffect(() => {
    setSerendipityActionInContext(goUp);
  }, [id, goUp]);

  const handleSwiped = () => {
    setDeltaToStart(null);
    if (Math.abs(positionY) > deltaToScroll) {
      if (positionY > 0) {
        goDown();
      } else {
        goUp();
      }
    } else {
      setIsSwiping(false);
    }
  };

  let toScroll = null;

  const handleScrollStop = () => {
    setDeltaToStart(null);
    setIsScrolling(false);
  };

  const handleScroll = () => {
    setIsScrolling(true);
    if (toScroll) {
      clearTimeout(toScroll);
    }

    toScroll = setTimeout(handleScrollStop, 150);
  };

  const handlers = useSwipeable({
    onSwiping: handleSwiping,
    onSwiped: handleSwiped,
    onTap: handleSwiped,
    delta: 30,
    // preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  // Handle keyboard navigation
  const checkKeyboardHandler = (event) => {
    switch (event.keyCode) {
      case 39:
        // Arrow right
        if (document.activeElement !== 'text') {
          goUp();
        }
        break;
      case 37:
        // Arrow left
        if (document.activeElement !== 'text') {
          goDown();
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', checkKeyboardHandler, false);
    return (() => {
      document.removeEventListener('keydown', checkKeyboardHandler, false);
    });
  });

  // Print

  const ActualStepPrint = useMemo(() => {
    if (steps.length < actualStep + 1) {
      if (isTheEnd) {
        return (<FinalStep id={id} />);
      }
      return (
        <LoadingWrapper>
          <Loading colorAnimation='#ffffff' />
        </LoadingWrapper>
      );
    }

    const step = steps[actualStep];
    pushStepAsViewed(step.id);
    return (
      <NarrativeStep
        onChangeAnswers={setAnswers}
        onFinish={goUp}
        data={step}
      />);
  }, [steps, actualStep, isTheEnd, id]);


  let colorButtons = actualContext ? actualContext.color : '#444';
  if (typeof colorButtons !== 'string') {
    colorButtons = colorButtons.color;
  }
  const offsetY = isSwiping || isChangingStep ? positionY : 0;
  const classes = ['step_content'];

  if (isSwiping) {
    classes.push('is_swiping');
  }

  return (
    <NarrativeStyled className='narrative_step'>
      <div className='overflow_step'>
        <StepContent className={classes.join(' ')} {...handlers} style={{ marginTop: `${offsetY}px` }} onScroll={handleScroll}>
          {ActualStepPrint}
        </StepContent>
      </div>
      <Buttons color={colorButtons}>
        <div className='prev_button' onClick={goDown}>
          <i className='fas fa-chevron-left' />
          {literals.previous}
        </div>
        <div className='next_button' onClick={goUp}>
          {literals.next}
          <i className='fas fa-chevron-right' />
        </div>
      </Buttons>
    </NarrativeStyled>
  );
}

Narrative.propTypes = {
  id: PropTypes.number.isRequired,
  literals: PropTypes.object.isRequired,
  setSerendipityActionInContext: PropTypes.func.isRequired,
  getSerendipityAction: PropTypes.func.isRequired,
  actualContext: PropTypes.object,
};

Narrative.defaultProps = {
  actualContext: null,
};

export default Narrative;



// WEBPACK FOOTER //
// ./src/components/Narrative/components/index.jsx